<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const BASE_URL = process.env.VUE_APP_EHTALK_URL || 'https://ehtalk.com';
const CHAVE_POS = 'creditall_ehtalk_pos';
const MARGEM = 12; // respiro mínimo das bordas da tela
const ARRASTE_MIN = 4; // px a partir do qual é arraste, não clique
const TAMANHO = 68; // diâmetro do balão, usado para limitar à tela

const aberto = ref(false);
const jaAbriu = ref(false);

const chatUrl = computed(() => (jaAbriu.value ? `${BASE_URL}/atendimento` : 'about:blank'));

/* ---------- posição (o balão é arrastável) ---------- */
// Guardamos a distância até as bordas direita/inferior, que é como o CSS posiciona.
const pos = ref({ right: 24, bottom: 24 });
const arrastando = ref(false);
let moveu = false;
let inicio = null;

const estilo = computed(() => ({
  right: `${pos.value.right}px`,
  bottom: `${pos.value.bottom}px`,
}));

// Sem posição salva, o balão sobe se a barra de contato fixa estiver na tela.
function posicaoPadrao() {
  const temBarra = document.body.classList.contains('tem-barra-contato');
  return { right: 24, bottom: temBarra ? 96 : 24 };
}

// Mantém o balão dentro da tela, inclusive depois de redimensionar a janela.
function limitar(p) {
  const maxRight = Math.max(MARGEM, window.innerWidth - TAMANHO - MARGEM);
  const maxBottom = Math.max(MARGEM, window.innerHeight - TAMANHO - MARGEM);
  return {
    right: Math.min(Math.max(p.right, MARGEM), maxRight),
    bottom: Math.min(Math.max(p.bottom, MARGEM), maxBottom),
  };
}

function carregarPosicao() {
  try {
    const bruto = localStorage.getItem(CHAVE_POS);
    if (bruto) {
      const p = JSON.parse(bruto);
      if (typeof p.right === 'number' && typeof p.bottom === 'number') {
        pos.value = limitar(p);
        return;
      }
    }
  } catch (e) {
    // localStorage pode falhar (aba privada/bloqueada) — cai no padrão.
  }
  pos.value = limitar(posicaoPadrao());
}

function salvarPosicao() {
  try {
    localStorage.setItem(CHAVE_POS, JSON.stringify(pos.value));
  } catch (e) {
    // a posição é conveniência; não persistir não quebra nada.
  }
}

function aoPressionar(evento) {
  if (evento.button !== undefined && evento.button !== 0) return; // só botão principal
  arrastando.value = true;
  moveu = false;
  inicio = {
    x: evento.clientX,
    y: evento.clientY,
    right: pos.value.right,
    bottom: pos.value.bottom,
  };
  window.addEventListener('pointermove', aoMover);
  window.addEventListener('pointerup', aoSoltar);
}

function aoMover(evento) {
  if (!arrastando.value || !inicio) return;
  const dx = inicio.x - evento.clientX; // "right" cresce para a esquerda
  const dy = inicio.y - evento.clientY; // "bottom" cresce para cima
  if (Math.abs(dx) > ARRASTE_MIN || Math.abs(dy) > ARRASTE_MIN) moveu = true;
  pos.value = limitar({ right: inicio.right + dx, bottom: inicio.bottom + dy });
}

function aoSoltar() {
  arrastando.value = false;
  inicio = null;
  window.removeEventListener('pointermove', aoMover);
  window.removeEventListener('pointerup', aoSoltar);
  if (moveu) salvarPosicao();
}

/* ---------- abrir/fechar ---------- */
function abrir() {
  jaAbriu.value = true;
  aberto.value = true;
}

function fechar() {
  aberto.value = false;
}

function aoClicar() {
  if (moveu) {
    // Foi arraste, não clique: não abre o chat.
    moveu = false;
    return;
  }
  aberto.value ? fechar() : abrir();
}

function aoTeclar(evento) {
  if (evento.key === 'Escape' && aberto.value) fechar();
}

function aoRedimensionar() {
  pos.value = limitar(pos.value);
}

onMounted(() => {
  carregarPosicao();
  window.addEventListener('keydown', aoTeclar);
  window.addEventListener('resize', aoRedimensionar);
  // Permite abrir o chat programaticamente a partir de outros componentes
  // (ex.: ModalFaleConosco, opção "Já sou cliente").
  window.addEventListener('abrir-ehtalk', abrir);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', aoTeclar);
  window.removeEventListener('resize', aoRedimensionar);
  window.removeEventListener('pointermove', aoMover);
  window.removeEventListener('pointerup', aoSoltar);
  window.removeEventListener('abrir-ehtalk', abrir);
});
</script>

<template>
  <div
    class="ehtalk-widget"
    :class="{ 'ehtalk-widget--open': aberto, 'ehtalk-widget--arrastando': arrastando }"
    :style="estilo"
  >
    <button
      type="button"
      class="ehtalk-widget__btn"
      :aria-label="aberto ? 'Fechar atendimento' : 'Abrir atendimento (arraste para mover)'"
      :aria-expanded="aberto ? 'true' : 'false'"
      title="Fale com o atendimento — arraste para mover"
      @pointerdown="aoPressionar"
      @click="aoClicar"
    >
      <i class="bi" :class="aberto ? 'bi-x-lg' : 'bi-whatsapp'"></i>
    </button>

    <div class="ehtalk-widget__panel" :hidden="!aberto">
      <div class="ehtalk-widget__header">
        <span>Atendimento</span>
        <button type="button" class="ehtalk-widget__close" aria-label="Fechar" @click="fechar">
          &times;
        </button>
      </div>
      <div class="ehtalk-widget__body">
        <iframe
          class="ehtalk-widget__iframe"
          title="Atendimento Creditall"
          allow="microphone"
          :src="chatUrl"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ehtalk-widget {
  position: fixed;
  z-index: 9999;
  /* right/bottom vêm do :style — o balão é arrastável. */
}

.ehtalk-widget__btn {
  position: relative;
  width: 68px;
  height: 68px;
  border-radius: 50%;
  border: 0;
  cursor: grab;
  touch-action: none; /* sem isso o arraste no toque vira rolagem da página */
  background-image: linear-gradient(to bottom, #25d366, #1ebe5d);
  color: #fff;
  font-size: 30px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
  transition: background-image 0.15s ease, transform 0.15s ease;
}

.ehtalk-widget__btn:hover {
  background-image: linear-gradient(to bottom, #1ebe5d, #17a34e);
  transform: scale(1.04);
}

.ehtalk-widget--arrastando .ehtalk-widget__btn {
  cursor: grabbing;
  transform: scale(1.08);
}

/* O painel é absoluto em relação ao balão, então acompanha quando ele é movido. */
.ehtalk-widget__panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 12px);
  width: 460px;
  height: 70vh;
  max-height: 640px;
  min-height: 420px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(16px) scale(0.98);
  opacity: 0;
  pointer-events: none;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.ehtalk-widget--open .ehtalk-widget__panel {
  transform: translateY(0) scale(1);
  opacity: 1;
  pointer-events: auto;
}

.ehtalk-widget__header {
  background-image: linear-gradient(to bottom, #25d366, #1ebe5d);
  color: #fff;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ehtalk-widget__close {
  background: transparent;
  border: 0;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
}

/* min-height:0 é obrigatório: sem ele o body estoura o painel e gera rolagem vertical dupla */
.ehtalk-widget__body {
  flex: 1;
  min-height: 0;
}

.ehtalk-widget__iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

@media (max-width: 640px) {
  .ehtalk-widget__panel {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    max-height: none;
    min-height: 0;
    border-radius: 0;
  }
}
</style>
